/*
 * GLX-084 | Cart Drawer Free Shipping Progress Bar
 * Variation 1
 *
 * Replaces the GLX-004 "Cart > Shipping > Payment" steps text with a
 * dynamic "RX away from free shipping" message and resizes the progress
 * fill bar based on the current cart total.
 *
 * Performance note: scoped MutationObserver to the cart-drawer element
 * (not document.body), and rAF-throttled so we don't recompute on every
 * micro-mutation inside the drawer.
 */
(async () => {
  const debugMode = 1;
  const testId = "GLX-084";
  const variation = 1;

  // Free-shipping threshold in major currency units (R for ZAR).
  // Pulled from the theme setting (Theme settings → Cart → Free shipping bar →
  // Minimum amount). Falls back to 900 if the theme didn't expose it.
  const FREE_SHIPPING_THRESHOLD = (typeof window !== "undefined" && window.themeFreeShippingThreshold) || 900;

  try {
    if (document.documentElement.classList.contains(`${testId}_var_${variation}`)) return;
    document.documentElement.classList.add(`${testId}_var_${variation}`);
    document.documentElement.classList.add(`${testId}_var`);

    const parsePrice = (priceText) => {
      if (!priceText) return null;
      const currencyMatch = priceText.match(/^[^\d]+/);
      const currency = currencyMatch ? currencyMatch[0].trim() : "";
      const numberText = priceText.replace(/[^\d.,]/g, "").replace(/,/g, "");
      const value = parseFloat(numberText);
      if (Number.isNaN(value)) return null;
      const decimals = (numberText.split(".")[1] || "").length;
      return { value, currency, decimals };
    };

    const formatNumber = (value, decimals) =>
      value.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

    const updateFreeShippingProgress = (cartDrawer) => {
      const checkoutProgress = cartDrawer.querySelector(".GLX-004-checkout-progress");
      if (!checkoutProgress) return;

      const progressFill =
        checkoutProgress.querySelector(".GLX-004-progress-bar .progress-fill") ||
        checkoutProgress.querySelector(".progress-fill");
      if (!progressFill) return;

      const checkoutSteps = checkoutProgress.querySelector(".GLX-004-checkout-steps");
      if (!checkoutSteps) return;

      const priceEl = cartDrawer.querySelector("span.cart-drawer__button-price");
      const priceInfo = parsePrice(priceEl ? priceEl.textContent : "");
      if (!priceInfo) return;

      const remaining = Math.max(FREE_SHIPPING_THRESHOLD - priceInfo.value, 0);
      const qualified = priceInfo.value >= FREE_SHIPPING_THRESHOLD;

      checkoutSteps.textContent = qualified
        ? "You qualified for free shipping!"
        : `${priceInfo.currency}${formatNumber(remaining, priceInfo.decimals)} away from free shipping`;

      const percent = Math.min((priceInfo.value / FREE_SHIPPING_THRESHOLD) * 100, 100);
      progressFill.style.width = `${percent}%`;
    };

    const waitForElement = (selector, timeoutMs = 30000) =>
      new Promise((resolve, reject) => {
        const existing = document.querySelector(selector);
        if (existing) return resolve(existing);
        let resolved = false;
        const observer = new MutationObserver(() => {
          const found = document.querySelector(selector);
          if (found) {
            resolved = true;
            observer.disconnect();
            resolve(found);
          }
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
        setTimeout(() => {
          if (!resolved) {
            observer.disconnect();
            reject(new Error(`waitForElement timeout for ${selector}`));
          }
        }, timeoutMs);
      });

    const cartDrawer = await waitForElement("cart-drawer#cart-drawer");

    // Cart drawer rerenders on every cart change — scope the observer to it,
    // not the entire body, and rAF-throttle.
    let pending = false;
    const scheduleUpdate = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        updateFreeShippingProgress(cartDrawer);
      });
    };

    new MutationObserver(scheduleUpdate).observe(cartDrawer, { childList: true, subtree: true });
    scheduleUpdate();
  } catch (err) {
    if (debugMode) console.error(`${testId} -->`, err);
  }
})();
